/* eslint-disable no-useless-return */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { ProfileField } from '../../types/profile';
import { CrmInviteData, CustomFieldResponses, CustomFieldType } from '../../types/invites';
import { crmService, connectionService, invitationService } from '../../services';
import CustomFieldsForm from '../../components/forms/CustomFieldsForm';
import UserDataForm from '../../components/forms/userData/UserDataForm';
import { checkMandatoryFields, isEmptyField } from '../../utils/validationUtils';
import useProfileFields from '../../hooks/useProfileFields';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import useDeleteParams from '../../hooks/useDeleteParams';
import { SuccessPageState } from '../../types/navigation';
import { createEmptyCustomFieldResponses } from '../../utils/formatUtils';
import CommunicationNameInput from '../../components/forms/profile/CommunicationNameInput';
import AddFieldsHeader from '../../components/headers/AddFieldsHeader';
import { filterPresentFields } from '../../utils/filterUtils';
import NoDataSharedModal from '../../components/modals/NoDataSharedModal';
import Button from '../../components/misc/Button';
import { SharedBusinessData } from '../../types/misc';
import NoRequestedDataCommunityInvitePage from '../common/NoRequestedDataCommunityInvitePage';
import { CommunityOverview } from '../../components/misc/CommunityOverview';

export default function AuthenticatedCrmUserInvitePage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const profileFields = useProfileFields();
  useDeleteParams('link');

  const token = params.get('invitationId');

  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>(
    createEmptyCustomFieldResponses([]),
  );
  const [selectedData, setSelectedData] = useState<ProfileField[]>([]);
  const [invite, setInvite] = useState<CrmInviteData>();
  const [validate, setValidate] = useState<boolean>(false);
  const [noDataSharedModalOpen, setNoDataSharedModalOpen] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error(t('toast.error.general.invalidInvite'));
      navigate('/');
      return;
    }

    invitationService
      .getCrmUserInvite(token)
      .then((inv) => {
        setInvite({ ...inv, receivedData: inv.receivedData?.map((f) => ({ ...f, id: v4() })) ?? [] });
        const preSelectedValues = filterPresentFields(
          profileFields,
          inv.receivedData?.filter((f) => !isEmptyField(f)),
        );

        setSelectedData(preSelectedValues);
        setCustomFieldResponses(createEmptyCustomFieldResponses(inv.customFields));
      })

      .catch(() => {
        toast.error(t('toast.error.general.inviteNotFound'));
        navigate('/');
      });
  }, []);

  const fetchedSharedData = useRef(false);
  useEffect(() => {
    if (!invite || profileFields.length === 0 || fetchedSharedData.current) return;
    fetchedSharedData.current = true;

    if (invite.businessId) {
      connectionService
        .getSharedDataWithBusiness(invite.businessId)
        .then((): void => {
          navigate('/success', {
            state: {
              translationKey: 'page.common.success.message.existingConnection',
              translationOptions: {
                requesterName: invite.name,
              },
              businessInvite: invite,
            },
          });
        })
        .catch((): void => {});
    } else if (invite.communityId) {
      connectionService
        .getSharedDataWithCommunity(invite.communityId)
        .then((): void => {
          navigate('/success', {
            state: {
              translationKey: 'page.common.success.message.existingConnection',
              translationOptions: {
                requesterName: invite.name,
              },
              businessInvite: invite,
            },
          });
        })
        .catch((): void => {});
    }
  }, [invite, profileFields]);

  const handleComplete = (): void => {
    if (selectedData.length === 0) setNoDataSharedModalOpen(true);
    else handleSubmit();
  };

  const handleSubmit = async (): Promise<void> => {
    setValidate(true);
    if (!checkMandatoryFields(invite!, selectedData, customFieldResponses)) return;
    const args: SharedBusinessData = {
      propertyIds: selectedData.map((x): string => x.id!),
      customFieldResponses,
      dataForMembers: [],
    };

    await crmService.acceptCrmUserInvite(token!, {
      propertyIds: args.propertyIds.map((x) => +x),
      customFieldResponses: args.customFieldResponses,
      dataForMembers: [],
    });

    const state: SuccessPageState = {
      translationKey: 'page.common.success.message.businessCommunityInvite',
      translationOptions: {
        businessName: invite?.name || '',
      },
      networkInvite: invite,
    };

    if (invite?.communityId && invite?.communityFeatures?.EASY_SHARING)
      navigate(`/communities/${invite.communityId}/members`, { state: { invite, success: state }, replace: true });
    else {
      navigate('/success', { state });
      toast.success(t('toast.success.connectionCreated'));
    }
  };

  const addFields = (fields: ProfileField[]): void => {
    setSelectedData([...selectedData.filter((f) => !fields.some((x) => x.dataType === f.dataType)), ...fields]);
  };

  if (invite && !invite.dashboardFeatures.MEMBER_ADMINISTRATION)
    return <NoRequestedDataCommunityInvitePage invite={invite!} onSubmit={handleSubmit} />;

  const requesterName = invite?.name || '';
  const communicationName = invite?.customFields.find((f) => f.type === CustomFieldType.COMMUNICATION_NAME);

  return (
    <>
      <ResponsePageWrapper>
        {invite && (
          <ResponsePageWrapper.SidePanel>
            <CommunityOverview invite={invite} />
          </ResponsePageWrapper.SidePanel>
        )}
        <ResponsePageWrapper.Header>
          <AddFieldsHeader requesterName={requesterName} fields={invite?.receivedData} addFields={addFields} />
        </ResponsePageWrapper.Header>
        <div className="flex h-full flex-1 flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-medium">{t('page.shared.requestedData')}</h2>
            <p className="text-sm">{t('page.businessInvite.message', { name: requesterName })}</p>
          </div>

          {communicationName && (
            <div className="border-panel-broder border-b pb-4">
              <h2 className="mb-1 border-t border-secondary-50 pt-4 text-lg font-medium">
                {t('form.communicationName.label')} {communicationName?.mandatory && '*'}
              </h2>
              <CommunicationNameInput
                name={customFieldResponses[communicationName.customFieldId]}
                validate={validate}
                setName={(name) =>
                  setCustomFieldResponses({
                    ...customFieldResponses,
                    [communicationName.customFieldId]: name,
                  })
                }
                mandatory={communicationName?.mandatory}
              />
            </div>
          )}
          <UserDataForm
            selectedData={selectedData}
            setSelectedData={setSelectedData}
            mandatoryFields={invite?.mandatoryFields || []}
            nonRequestedFields={invite?.nonRequestedFields || []}
            onePerDataType
            onlyBirthDate
          />

          <div>
            {invite?.customFields && invite?.customFields.length > 0 && (
              <>
                <h2 className="mb-1 border-t border-secondary-50 pt-4 text-lg font-medium">
                  {t('page.shared.requestedFields')}
                </h2>
                <CustomFieldsForm
                  fields={invite?.customFields || []}
                  response={customFieldResponses}
                  setResponse={setCustomFieldResponses}
                />
              </>
            )}
          </div>
          <div className="flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
            <Button variant="primary" className="px-[32px]" onClick={handleComplete}>
              {t('page.shared.shareData')}
            </Button>
          </div>
        </div>
      </ResponsePageWrapper>
      <NoDataSharedModal
        open={noDataSharedModalOpen}
        setOpen={setNoDataSharedModalOpen}
        requesterName={requesterName}
        handleComplete={handleSubmit}
      />
    </>
  );
}
